import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, Mail, ArrowLeft } from 'lucide-react'
import { forgotPasswordApi } from '../api/auth.api'

const forgotPasswordSchema = z.object({
  email: z.string().email(),
})

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>

export function ForgotPassword() {
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    setError(null)
    try {
      await forgotPasswordApi(values.email)
      setSuccess(true)
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } }
      setError(axiosError.response?.data?.message || t('auth.forgotPassword.error'))
    }
  }

  if (success) {
    return (
      <div className="space-y-8">
        <div className="text-center space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight text-neutral-900">
              {t('auth.forgotPassword.successTitle')}
            </h1>
            <p className="text-sm text-neutral-500 leading-relaxed max-w-sm mx-auto">
              {t('auth.forgotPassword.successMessage')}
            </p>
          </div>
        </div>
        <Link
          to="/login"
          className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-neutral-300 bg-white px-6 text-sm font-medium text-neutral-700 transition-all hover:bg-neutral-50"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20">
          <Mail className="h-8 w-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight text-neutral-900">
            {t('auth.forgotPassword.title')}
          </h1>
          <p className="text-sm text-neutral-500 leading-relaxed max-w-sm mx-auto">
            {t('auth.forgotPassword.subtitle')}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {error && (
          <div className="rounded-lg bg-error/10 border border-error/20 px-4 py-3 text-sm text-error">
            {error}
          </div>
        )}

        {/* Email */}
        <div className="space-y-1.5">
          <label htmlFor="email" className="block text-sm font-medium text-neutral-700">
            {t('auth.forgotPassword.email')}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">
              <Mail className="h-4 w-4" />
            </span>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="nom@exemple.com"
              className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 pl-10 pr-4 text-sm transition-all placeholder:text-neutral-400 hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 disabled:opacity-50"
              {...register('email')}
            />
          </div>
          {errors.email && (
            <p className="text-xs text-error">{errors.email.message}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-gradient-to-r from-primary to-primary-800 px-6 text-sm font-semibold text-white shadow-lg shadow-primary/30 transition-all hover:from-primary-800 hover:to-primary-950 hover:shadow-xl hover:shadow-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('auth.forgotPassword.submitting')}
            </>
          ) : (
            t('auth.forgotPassword.submit')
          )}
        </button>
      </form>

      {/* Back to login link */}
      <div className="text-center">
        <Link to="/login" className="text-sm text-primary hover:text-primary-800 transition-colors">
          <ArrowLeft className="mr-1 inline h-3 w-3" />
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </div>
    </div>
  )
}
